import React, { useMemo, useState } from 'react';
import { ClipboardPaste, Trash2, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { CompatPasteModal } from './CompatPasteModal';
import { adminAPI } from '../services/api';

const groupFitments = (rows) => {
  const brands = {};
  rows.forEach((r) => {
    const models = (brands[r.brand] = brands[r.brand] || {});
    const key = `${r.model} ${r.model_years || ''}`.trim();
    (models[key] = models[key] || { model: r.model, model_years: r.model_years, engines: [] }).engines.push(r);
  });
  return Object.entries(brands).sort().map(([brand, models]) => ({ brand, models: Object.values(models) }));
};

export const FitmentsEditor = ({ fitments, onChange, onRules, productInfo = null }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const grouped = useMemo(() => groupFitments(fitments), [fitments]);

  const apply = (result, mode) => {
    const next = mode === 'replace' ? result.fitments : [...fitments, ...result.fitments.filter((f) => !fitments.some((x) => x.brand === f.brand && x.model === f.model && x.engine === f.engine))];
    onChange(next);
    onRules?.(result.compatibility, mode);
  };

  const removeModel = (brand, model) => onChange(fitments.filter((f) => !(f.brand === brand && f.model === model)));

  return (
    <div data-testid="fitments-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardPaste size={18} className="text-orange-500" /> Véhicules compatibles (liste détaillée)</h3>
          <p className="text-xs text-gray-500" data-testid="fitments-count">
            {fitments.length === 0 ? 'Aucune ligne : collez la liste des véhicules compatibles pour l\'afficher sur la fiche.' : `${fitments.length} motorisation(s) · ${grouped.length} marque(s) — affichées sur la fiche produit.`}
          </p>
        </div>
        <div className="flex gap-2">
          {fitments.length > 0 && (
            <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => onChange([])} data-testid="fitments-clear"><Trash2 size={14} className="mr-1" /> Tout effacer</Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="fitments-paste-btn"><ClipboardPaste size={14} className="mr-1" /> Coller une liste{productInfo?.brand && productInfo?.ref ? ' / IA' : ''}</Button>
        </div>
      </div>
      {grouped.length > 0 && (
        <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
          {grouped.map((b) => {
            const isOpen = expanded === b.brand;
            return (
              <div key={b.brand} data-testid={`fitments-brand-${b.brand}`}>
                <button type="button" onClick={() => setExpanded(isOpen ? null : b.brand)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50">
                  <span>{b.brand} <span className="text-xs text-gray-500 font-normal">· {b.models.length} modèle(s)</span></span>
                  <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-3 pb-2 space-y-1.5">
                    {b.models.map((m) => (
                      <div key={m.model + m.model_years} className="flex items-start gap-2">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-800">{m.model} {m.model_years && <span className="text-gray-400">{m.model_years}</span>}</p>
                          <ul className="flex flex-wrap gap-1 mt-0.5">
                            {m.engines.filter((e) => e.engine).map((e, i) => <li key={i} className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{e.engine}{e.engine_years ? <span className="text-gray-400"> · {e.engine_years}</span> : null}</li>)}
                          </ul>
                        </div>
                        <button type="button" onClick={() => removeModel(b.brand, m.model)} className="p-1 text-gray-400 hover:text-red-600" title="Retirer ce modèle" data-testid={`fitments-remove-${m.model}`}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <CompatPasteModal open={open} onClose={() => setOpen(false)} hasExisting={fitments.length > 0} onApply={apply} aiSuggest={productInfo?.brand && productInfo?.ref ? () => adminAPI.compatAiSuggest(productInfo) : null} />
    </div>
  );
};

export default FitmentsEditor;
